import React, { useEffect, useState, useCallback } from 'react';
import Card from '../ui/Card';
import { RescheduleModal, CancelConfirmModal, BookModal } from './ScheduleActionModals';

const API = process.env.REACT_APP_API_URL || '';

function localDateStr(offsetDays = 0) {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return d.toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
}

function fmt12(isoStr) {
  if (!isoStr) return '';
  return new Date(isoStr).toLocaleTimeString('en-US', {
    hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'America/New_York',
  });
}

function fmtDayHeading(dateStr) {
  const today = localDateStr(0);
  if (dateStr === today) return 'Today';
  const d = new Date(dateStr + 'T12:00:00');
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
}

function fmtNextOpening(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr + 'T12:00:00');
  if (isNaN(d)) return dateStr;
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

function fmtSyncTime(iso) {
  if (!iso) return null;
  return new Date(iso).toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true,
  });
}

function typeShort(type) {
  if (!type) return 'Individual';
  if (/couples/i.test(type)) return 'Couples';
  if (/family/i.test(type)) return 'Family';
  if (/intake|new client/i.test(type)) return 'New Client';
  if (/group/i.test(type)) return 'Group';
  if (/supervision/i.test(type)) return 'Supervision';
  if (/KAP|ketamine/i.test(type)) return 'KAP';
  if (/EMDR/i.test(type)) return 'EMDR';
  return 'Individual';
}

const TYPE_COLOR = {
  Couples: '#8338EC', Family: '#E07A5F', 'New Client': '#2A9D8F',
  Group: '#F4A261', Supervision: '#118AB2', KAP: '#D62246',
  EMDR: '#06A77D', Individual: 'var(--primary-color)',
};

const menuItemStyle = { display: 'block', width: '100%', textAlign: 'left', background: 'transparent', border: 'none', padding: '8px 12px', fontSize: 13, cursor: 'pointer', color: 'var(--text-strong)' };

function StatBox({ label, value, sub, accent, onClick }) {
  const [hovered, setHovered] = React.useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => onClick && setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        flex: 1, minWidth: 120, borderRadius: 10,
        padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 3,
        borderTop: `3px solid ${accent || 'var(--primary-color)'}`,
        background: hovered ? '#EDE8E1' : 'var(--secondary-color)',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'background 0.15s',
      }}
    >
      <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--muted-color)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        {label}
      </div>
      <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--primary-color)', lineHeight: 1.2 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--muted-color)' }}>{sub}</div>}
      {onClick && <div style={{ fontSize: 10, color: accent || 'var(--accent-color)', marginTop: 2, fontWeight: 600 }}>tap to see all →</div>}
    </div>
  );
}

// calendarId prop = admin view for a specific therapist
// omit calendarId = therapist sees their own schedule via /my-schedule
// embedded = rendered inside a dashboard WidgetShell (which provides the title
// bar) — skip the internal navy header; last-sync time moves into a slim row.
// actions = interactive mode: per-appointment reschedule/cancel (+ Book in
// the toolbar) — only the dashboards pass this; all other uses stay read-only.
export default function ScheduleCard({ calendarId, embedded = false, actions = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);
  const [dayOffset, setDayOffset] = useState(0);
  const [showCanceled, setShowCanceled] = useState(false);
  const [bookOpen, setBookOpen] = useState(false);

  const [openingsModal, setOpeningsModal] = useState({ show: false, loading: false, items: [] });
  const [actionTarget, setActionTarget] = useState(null); // { kind: 'reschedule'|'cancel', appt }
  const [menuFor, setMenuFor] = useState(null); // acuity_id of the row whose ⋯ menu is open

  const endpoint = calendarId
    ? `${API}/api/acuity/schedule/${calendarId}`
    : `${API}/api/acuity/my-schedule`;

  const openingsEndpoint = calendarId
    ? `${API}/api/acuity/next-openings/${calendarId}`
    : `${API}/api/acuity/my-next-openings`;

  const load = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(endpoint, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Failed to load');
      const json = await res.json();
      setData(json);
      setError(null);
    } catch {
      setError('Could not load schedule data.');
    } finally {
      setLoading(false);
    }
  }, [endpoint]);

  const fetchOpenings = async () => {
    setOpeningsModal({ show: true, loading: true, items: [] });
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(openingsEndpoint, { headers: { Authorization: `Bearer ${token}` } });
      const json = await res.json();
      setOpeningsModal({ show: true, loading: false, items: Array.isArray(json) ? json : [] });
    } catch {
      setOpeningsModal({ show: true, loading: false, items: [] });
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API}/api/acuity/sync`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      await load();
    } catch {
      setError('Sync failed.');
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => { load(); }, [load]);

  // Close the appointment ⋯ menu on any click outside it.
  useEffect(() => {
    if (menuFor === null) return undefined;
    const onDocMouseDown = (e) => {
      if (!e.target.closest('[data-appt-menu]')) setMenuFor(null);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [menuFor]);

  const viewDate = localDateStr(dayOffset);
  const allViewAppts = data?.appointmentsByDay?.[viewDate] || [];
  const viewAppts = showCanceled ? allViewAppts : allViewAppts.filter((a) => !a.canceled);
  const canceledCount = allViewAppts.filter((a) => a.canceled).length;
  const dayLabel = fmtDayHeading(viewDate);

  // Derive display name from data or user storage
  const user = (() => { try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; } })();
  const displayName = data?.calendarName
    ? data.calendarName.split(',')[0].trim()
    : calendarId ? 'Schedule' : `${user.first_name || ''} ${user.last_name || ''}`.trim();

  const closeModal = () => setOpeningsModal(s => ({ ...s, show: false }));

  if (data?.noCalendar) {
    return (
      <Card style={{ overflow: 'hidden', marginBottom: embedded ? 0 : 24, padding: 0 }}>
        {!embedded && (
          <div style={{ background: 'var(--primary-color)', padding: '14px 20px' }}>
            <div style={{ color: '#fff', fontWeight: 700, fontSize: 15 }}>My Schedule</div>
          </div>
        )}
        <div style={{ padding: '20px', color: 'var(--muted-color)', fontSize: 13 }}>
          No Acuity calendar connected to your account yet.
        </div>
      </Card>
    );
  }

  return (
    <>
    {openingsModal.show && (
      <div
        onClick={closeModal}
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <div
          onClick={e => e.stopPropagation()}
          style={{ background: '#fff', borderRadius: 12, padding: 24, width: '90%', maxWidth: 420, maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 4px 24px rgba(0,0,0,0.18)' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
            <span style={{ fontWeight: 700, fontSize: 16, color: 'var(--primary-color)' }}>Next Openings</span>
            <button onClick={closeModal} style={{ background: 'none', border: 'none', fontSize: 22, lineHeight: 1, cursor: 'pointer', color: 'var(--muted-color)', padding: 0 }}>×</button>
          </div>

          {openingsModal.loading ? (
            <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--muted-color)', fontSize: 13 }}>Loading openings…</div>
          ) : openingsModal.items.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--muted-color)', fontSize: 13 }}>No openings found in the current sync window.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              {openingsModal.items.map(({ date, times }) => (
                <div key={date}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--primary-color)', marginBottom: 8, borderBottom: '1px solid #F0EDE8', paddingBottom: 5 }}>
                    {new Date(date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {times.map(t => (
                      <span key={t} style={{ fontSize: 12, background: '#FFF3ED', color: 'var(--accent-color)', borderRadius: 6, padding: '4px 10px', fontWeight: 600 }}>
                        {fmt12(t)}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    )}
    <Card style={{ overflow: 'hidden', marginBottom: embedded ? 0 : 24, padding: 0 }}>

      {/* Header (slim info row instead when embedded in a widget shell) */}
      {!embedded ? (
        <div style={{ background: 'var(--primary-color)', padding: '14px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ color: '#fff', fontWeight: 700, fontSize: 15 }}>{displayName} — Schedule</div>
            {data?.lastSync && (
              <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: 11, marginTop: 2 }}>
                Updated {fmtSyncTime(data.lastSync)}
              </div>
            )}
          </div>
          {calendarId && (
            <button
              onClick={handleSync}
              disabled={syncing}
              style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 6, padding: '5px 13px', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}
            >
              {syncing ? 'Syncing…' : '↻ Sync'}
            </button>
          )}
        </div>
      ) : (
        (data?.lastSync || calendarId || actions) && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 10, padding: '8px 14px 0' }}>
            {actions && (
              <button
                type="button"
                onClick={() => setBookOpen(true)}
                style={{ background: 'var(--accent-color)', color: '#fff', border: 'none', borderRadius: 6, padding: '4px 12px', fontSize: 12, cursor: 'pointer', fontWeight: 600, marginRight: 'auto' }}
              >
                + Book
              </button>
            )}
            {data?.lastSync && (
              <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>
                Updated {fmtSyncTime(data.lastSync)}
              </span>
            )}
            {calendarId && (
              <button
                onClick={handleSync}
                disabled={syncing}
                style={{ background: 'var(--primary-color)', color: '#fff', border: 'none', borderRadius: 6, padding: '4px 12px', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}
              >
                {syncing ? 'Syncing…' : '↻ Sync'}
              </button>
            )}
          </div>
        )
      )}

      {loading && <div style={{ padding: 24, color: 'var(--muted-color)', fontSize: 13 }}>Loading schedule…</div>}
      {error && !loading && (
        <div style={{ padding: '16px 20px' }}>
          <p style={{ color: 'var(--error-color)', fontSize: 13, margin: calendarId ? '0 0 10px' : 0 }}>{error}</p>
          {calendarId && (
            <button onClick={handleSync} disabled={syncing} style={{ background: 'var(--primary-color)', color: '#fff', border: 'none', borderRadius: 6, padding: '7px 16px', cursor: 'pointer', fontSize: 13 }}>
              {syncing ? 'Syncing…' : 'Sync Now'}
            </button>
          )}
        </div>
      )}

      {!loading && data && (
        <div style={{ display: 'flex', gap: 0, flexWrap: 'wrap' }}>

          {/* Left — Day schedule with navigator */}
          <div style={{ flex: '1 1 300px', borderRight: '1px solid #EEF0F6', padding: '18px 20px', display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <button
                onClick={() => setDayOffset(o => Math.max(0, o - 1))}
                disabled={dayOffset === 0}
                style={{ background: 'none', border: '1px solid var(--border-color)', borderRadius: 6, width: 30, height: 30, cursor: dayOffset === 0 ? 'default' : 'pointer', color: dayOffset === 0 ? 'var(--border-color)' : 'var(--primary-color)', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >‹</button>
              <div style={{ textAlign: 'center', flex: 1, padding: '0 8px' }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--primary-color)' }}>{dayLabel}</div>
                {dayOffset === 0 && <div style={{ fontSize: 11, color: 'var(--muted-color)', marginTop: 1 }}>Tap › to peek ahead</div>}
              </div>
              <button
                onClick={() => setDayOffset(o => Math.min(5, o + 1))}
                disabled={dayOffset === 5}
                style={{ background: 'none', border: '1px solid var(--border-color)', borderRadius: 6, width: 30, height: 30, cursor: dayOffset === 5 ? 'default' : 'pointer', color: dayOffset === 5 ? 'var(--border-color)' : 'var(--primary-color)', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >›</button>
            </div>

            <div style={{ display: 'flex', justifyContent: 'center', gap: 5, marginBottom: 14 }}>
              {[0,1,2,3,4,5].map(i => (
                <button key={i} onClick={() => setDayOffset(i)}
                  style={{ width: i === dayOffset ? 18 : 7, height: 7, borderRadius: 4, background: i === dayOffset ? 'var(--primary-color)' : 'var(--border-color)', border: 'none', cursor: 'pointer', padding: 0, transition: 'all 0.2s' }}
                />
              ))}
            </div>

            {viewAppts.length === 0 ? (
              <p style={{ fontSize: 13, color: '#aaa', margin: 0 }}>
                {!data.lastSync ? 'No data yet — click Sync.' : 'No appointments.'}
              </p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 9, flex: 1 }}>
                {viewAppts.map((appt, i) => {
                  const label = typeShort(appt.appointment_type);
                  const color = TYPE_COLOR[label] || 'var(--primary-color)';
                  const strike = appt.canceled ? 'line-through' : 'none';
                  return (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, opacity: appt.canceled ? 0.55 : 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-strong)', minWidth: 74, textDecoration: strike }}>{fmt12(appt.appt_datetime)}</div>
                      <div style={{ fontSize: 13, color: '#444', flex: 1, textDecoration: strike }}>{appt.client_first_name} {appt.client_last_name?.charAt(0)}.</div>
                      {actions && appt.acuity_id && !appt.canceled && (
                        <div style={{ position: 'relative' }} data-appt-menu="true">
                          <button
                            type="button"
                            onClick={() => setMenuFor(menuFor === appt.acuity_id ? null : appt.acuity_id)}
                            aria-label="Appointment actions"
                            style={{ background: 'var(--surface-muted)', color: 'var(--text-strong)', border: '1px solid var(--border-color)', borderRadius: 999, padding: '2px 10px', fontSize: 11, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}
                          >
                            Manage ▾
                          </button>
                          {menuFor === appt.acuity_id && (
                            <div style={{ position: 'absolute', right: 0, top: '110%', zIndex: 20, background: 'var(--white)', border: '1px solid var(--border-color)', borderRadius: 8, boxShadow: '0 6px 18px rgba(0,0,0,0.15)', minWidth: 150 }}>
                              <button type="button" style={menuItemStyle} onClick={() => { setMenuFor(null); setActionTarget({ kind: 'reschedule', appt }); }}>
                                ↔ Reschedule
                              </button>
                              <button type="button" style={{ ...menuItemStyle, color: 'var(--error-color)' }} onClick={() => { setMenuFor(null); setActionTarget({ kind: 'cancel', appt }); }}>
                                ✕ Cancel
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                      {appt.canceled ? (
                        <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--error-color)', background: 'rgba(217,83,79,0.12)', borderRadius: 10, padding: '2px 8px', whiteSpace: 'nowrap' }}>Canceled</span>
                      ) : (
                        <span style={{ fontSize: 11, fontWeight: 600, color, background: color + '18', borderRadius: 10, padding: '2px 8px', whiteSpace: 'nowrap' }}>{label}</span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            <div style={{ marginTop: 14, paddingTop: 10, borderTop: '1px solid #F0EDE8', fontSize: 12, color: 'var(--muted-color)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
              <span>{viewAppts.length} appointment{viewAppts.length !== 1 ? 's' : ''}</span>
              <label style={{ fontSize: 11, display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer', userSelect: 'none' }}>
                <input type="checkbox" checked={showCanceled} onChange={(e) => setShowCanceled(e.target.checked)} />
                Show canceled{canceledCount > 0 ? ` (${canceledCount})` : ''}
              </label>
            </div>
          </div>

          {/* Right — Stats */}
          <div style={{ flex: '1 1 260px', padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted-color)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>
              Next 7 Days
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <StatBox label="Appointments" value={data.appointmentsNext7} sub="scheduled" accent="var(--primary-color)" />
              <StatBox label="Openings" value={data.openings7} sub="available slots" accent="#2A9D8F" />
            </div>
            <div style={{ marginTop: 6 }}>
              <StatBox
                label="Next Opening"
                value={fmtNextOpening(data.nextOpeningDate)}
                sub={data.nextOpeningTime ? fmt12(data.nextOpeningTime) : (data.nextOpeningDate ? '' : 'none found in range')}
                accent="var(--accent-color)"
                onClick={data.nextOpeningDate ? fetchOpenings : undefined}
              />
            </div>
          </div>

        </div>
      )}
    </Card>
    {actionTarget?.kind === 'reschedule' && (
      <RescheduleModal
        appt={actionTarget.appt}
        calendarId={data?.calendarId}
        typeId={actionTarget.appt.appointment_type_id || data?.primaryApptTypeId}
        onDone={load}
        onClose={() => setActionTarget(null)}
      />
    )}
    {actionTarget?.kind === 'cancel' && (
      <CancelConfirmModal
        appt={actionTarget.appt}
        calendarId={data?.calendarId}
        onDone={load}
        onClose={() => setActionTarget(null)}
      />
    )}
    {bookOpen && (
      <BookModal
        calendarId={data?.calendarId}
        onDone={load}
        onClose={() => setBookOpen(false)}
      />
    )}
    </>
  );
}
